import {
	PRODUCT_ITEM_KIND_LABELS,
	PRODUCT_ITEM_KINDS,
	type ProductItemKind,
} from "@shared/types";
import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { StatusBadge } from "@/components/ui/status-badge";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { BulkChangeForm } from "@/features/catalog/components/bulk-change-form";
import {
	BulkConfirmDialog,
	bulkIntentSentence,
	type PendingBulkSummary,
} from "@/features/catalog/components/bulk-confirm-dialog";
import { BulkRefusalList } from "@/features/catalog/components/bulk-refusal-list";
import { CategoryPicker } from "@/features/catalog/components/category-picker";
import { DataTable } from "@/features/catalog/components/data-table";
import { isPriceStale, priceLabel } from "@/features/catalog/lib/format";
import {
	type BulkProductPatch,
	type BulkUpdateResult,
	bulkUpdateProducts,
	listAdminBrands,
	listAdminCategories,
	listAdminProducts,
	MAX_BULK_SELECTION,
	PAGE_SIZES,
	type PageSize,
	PER_PAGE,
	type ProductFilters,
	resolveSelection,
	scopeCatalogFilters,
} from "@/features/catalog/services/catalog-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { hasPermission } from "@/lib/permissions";
import { useDebounce } from "@/lib/use-debounce";

/**
 * The catalogue product list -- the first catalogue screen in the admin app.
 *
 * Reads the admin list endpoint rather than public search on purpose: the
 * Typesense index holds only `active` products, so the drafts an operator is
 * here to finish would be invisible through the browse path.
 *
 * The table shell is now `DataTable`, extracted when the brands list arrived --
 * which is the condition this comment used to set for extracting it. What the
 * two lists actually share turned out not to be columns but the accessibility
 * scaffolding: header scopes, per-row tab stops and labels, the keyboard
 * handler, the refetch dimming. Columns stayed here, where they differ.
 */

const ALL = "all";

/**
 * The values `?status=` may carry, which are `ProductStatus` and nothing else.
 *
 * Declared as a list rather than trusted from the URL: this is the filter's
 * allow-list, and the alternative is forwarding whatever a link contains to a
 * server that will reject it.
 */
const PRODUCT_STATUSES: string[] = ["draft", "active", "inactive"];
const ITEM_KINDS = PRODUCT_ITEM_KINDS;
const ITEM_KIND_LABELS = PRODUCT_ITEM_KIND_LABELS;

/**
 * Tabular page number window (up to 5 pages), clamping to ends when near boundaries.
 */
function getPageNumbers(currentPage: number, totalPages: number): number[] {
	if (totalPages <= 5) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}
	if (currentPage <= 3) {
		return [1, 2, 3, 4, 5];
	}
	if (currentPage >= totalPages - 2) {
		return [
			totalPages - 4,
			totalPages - 3,
			totalPages - 2,
			totalPages - 1,
			totalPages,
		];
	}
	return [
		currentPage - 2,
		currentPage - 1,
		currentPage,
		currentPage + 1,
		currentPage + 2,
	];
}

/**
 * What `?categoryId=` must look like before it is forwarded.
 *
 * The server validates it as a uuid, so an unchecked value turns a link into a
 * 400 on arrival. Shape only -- whether the category EXISTS is the server's
 * question, and an id that parses but matches nothing returns an empty list,
 * which reads correctly as "no products here".
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function CatalogProductsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	/*
	 * Selection is PER PAGE, and clearing it on any filter or page change is the
	 * point rather than an omission.
	 *
	 * A selection that survives a filter change is a selection the operator can
	 * no longer see. Bulk actions here move 500 products at once; "apply to
	 * everything I ticked, including the 40 rows now hidden behind a different
	 * status filter" is the shape of an accident nobody can review afterwards,
	 * because the audit records the change and not the intent.
	 */
	const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
	/*
	 * SELECT-ALL-MATCHING, which is a different KIND of selection.
	 *
	 * `selected` is a list of ticked ids and is bounded by the page. This is the
	 * filter itself, resolved to ids only when the action fires -- so it reaches
	 * the 500 the endpoint accepts without a selection the operator cannot see,
	 * because what is selected is exactly what the filter says on screen.
	 *
	 * It DROPS on any filter change rather than re-scoping to the new filter.
	 * Re-scoping sounds tidier and is dangerous in one direction: select all 342,
	 * press Clear filters, and "all matching" silently becomes every product in
	 * the catalogue, one click from Publish, with no number the operator ever
	 * read. Dropping is what the ticked-rows selection already does, so the two
	 * modes behave the same way at the moment that matters.
	 */
	const [matchingAll, setMatchingAll] = useState(false);
	const [excluded, setExcluded] = useState<ReadonlySet<string>>(new Set());
	const [pendingBulk, setPendingBulk] = useState<{
		patch: BulkProductPatch;
		ids: string[];
		/*
		 * The whole sentence, captured at the moment the dialog opened -- filter
		 * wording included. Freezing the count while passing the filter live meant
		 * a filter change rewrote half the sentence and left the other half, so
		 * the dialog could describe a scope its ids had never come from.
		 */
		summary: PendingBulkSummary;
	} | null>(null);
	/** True while `resolveSelection` is in flight, so the triggers can say so. */
	const [resolving, setResolving] = useState(false);
	const [resolveError, setResolveError] = useState<string | null>(null);
	const [bulkResult, setBulkResult] = useState<BulkUpdateResult | null>(null);
	const operator = useAdminAuthStore((state) => state.operator);
	const canWrite = hasPermission(operator, "admin:catalog:write");
	/*
	 * Publish is priced separately from write, and the bulk bar has to say so.
	 *
	 * The server charges `admin:catalog:publish` for `status` whether one product
	 * or five hundred is being published -- the bulk route reads the same field
	 * rules as the single PATCH. Offering a Publish button to a role that will be
	 * refused turns a permission boundary into an error message after the fact,
	 * on an action the operator had every reason to think would work.
	 */
	const canPublish = hasPermission(operator, "admin:catalog:publish");
	/*
	 * The whole set, for `BulkChangeForm` -- which prices five fields at three
	 * different permissions and so cannot be handed a pair of booleans.
	 */
	const operatorPermissions = new Set<string>(
		operator?.authorization?.permissions ?? [],
	);
	const [query, setQuery] = useState("");

	/*
	 * SEEDED FROM THE URL, so `?status=draft` is a filter another screen can send
	 * someone to. The dashboard's whole design is that every count is a link to
	 * the rows it counted; before this the filter lived only in local state, so
	 * such a link landed here showing everything and quietly did nothing.
	 *
	 * Seeded ONCE rather than driven by the URL. `setStatus` from the dropdown
	 * does not rewrite the query string, so the two would disagree the moment an
	 * operator changed the filter by hand -- and the URL, being the thing that
	 * looks authoritative, would be the wrong one. Making the select write back
	 * is a bigger change than this needs: it means owning page, query and brand
	 * in the URL too, or having one filter behave unlike its three neighbours.
	 *
	 * An unrecognised value falls back to ALL rather than being passed through.
	 * `ProductFilters["status"]` is a union the server also validates, so
	 * `?status=nonsense` would otherwise produce a 400 on a screen the operator
	 * reached by clicking a link.
	 */
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState<string>(() => {
		const requested = searchParams.get("status");
		return requested && PRODUCT_STATUSES.includes(requested) ? requested : ALL;
	});
	const [itemKind, setItemKind] = useState<string>(() => {
		const requested = searchParams.get("itemKind");
		if (requested === ALL) return ALL;
		return requested && (ITEM_KINDS as readonly string[]).includes(requested)
			? requested
			: ALL;
	});

	/*
	 * THE CATEGORY FILTER, seeded the same way and for the same reason.
	 *
	 * Its absence was deliberate for a while: 674 leaf categories are not a
	 * `<select>`, and the searchable typeahead this needs was only worth building
	 * once a second consumer wanted it. `CategoryPicker` exists now, built for
	 * the product editor, and its own docblock names this filter as that second
	 * consumer.
	 *
	 * `null` rather than the `ALL` sentinel the two selects use, because
	 * `CategoryPicker` speaks `string | null` and adapting it here would mean one
	 * filter behaving unlike its neighbours in a different way.
	 *
	 * A malformed id falls back to unfiltered. `adminListProductsSchema` validates
	 * `categoryId` as a uuid, so `?categoryId=nonsense` would otherwise 400 a
	 * screen the operator reached by clicking a link -- the same reasoning as
	 * `status` above.
	 */
	const [categoryId, setCategoryId] = useState<string | null>(() => {
		const requested = searchParams.get("categoryId");
		return requested && UUID.test(requested) ? requested : null;
	});
	/** Set when the operator picks; null when the id arrived in the URL. */
	const [pickedCategoryName, setPickedCategoryName] = useState<string | null>(
		null,
	);
	const [brandId, setBrandId] = useState<string>(ALL);
	const [page, setPage] = useState(1);
	/*
	 * The WINDOW, not the membership -- which is why it sits outside the key that
	 * drops matching mode. Widening from 25 to 100 shows more of the same
	 * selection; it does not change which products are in it, and dropping a
	 * 342-product selection because someone wanted to see more of it at once
	 * would be the same defect paging had.
	 */
	const [perPage, setPerPage] = useState<PageSize>(PER_PAGE);

	const debouncedQuery = useDebounce(query, 250);

	/*
	 * A URL-SEEDED CATEGORY HAS AN ID AND NO NAME, and `CategoryPicker` renders
	 * an empty box when it has no label -- so the list would come back filtered
	 * with nothing on screen saying to what. A filter that is applied and
	 * invisible is the shape of defect this screen has already shipped once.
	 *
	 * `listAdminCategories`, NOT `searchAdminCategories("")`, though both issue
	 * the same request. The picker's own docblock records that an empty search
	 * term was a mistake it was fixed for -- it returns the entire taxonomy plus
	 * an unfiltered product-count aggregate, and it was doing that on every focus
	 * of a field the editor always shows. Calling the function whose documented
	 * job is "the whole taxonomy" says what this is, and does not re-adopt a call
	 * another comment names as an error.
	 *
	 * The cost is real and the frequency is not: this runs when an id arrives
	 * WITHOUT a name, which is the link case only. Picking from the typeahead
	 * supplies the name and leaves this idle.
	 */
	const seededCategory = useQuery({
		queryKey: ["admin-catalog-taxonomy-for-label"],
		queryFn: listAdminCategories,
		enabled: categoryId !== null && pickedCategoryName === null,
		staleTime: 5 * 60_000,
	});

	const categoryLabel =
		pickedCategoryName ??
		seededCategory.data?.items.find((row) => row.id === categoryId)?.name ??
		null;

	const filters: ProductFilters = {
		q: debouncedQuery || undefined,
		status: status === ALL ? undefined : (status as ProductFilters["status"]),
		brandId: brandId === ALL ? undefined : brandId,
		categoryId: categoryId ?? undefined,
		itemKind: itemKind === ALL ? undefined : (itemKind as ProductItemKind),
		page,
		perPage,
	};

	/*
	 * The key is built from the SCOPED filters, not the requested ones.
	 *
	 * `scopeFilters` is a no-op today, so the two are identical and this looks
	 * like ceremony. It stops being ceremony the day the seam starts narrowing:
	 * a key built from what the screen ASKED for would collide across two
	 * operators whose scopes differ, and React Query would hand the second one
	 * the first one's cached rows. Keying on what was actually requested of the
	 * server is the only version that cannot do that.
	 */
	const scoped = scopeCatalogFilters(filters);

	const hasFilters =
		query !== "" ||
		status !== ALL ||
		brandId !== ALL ||
		categoryId !== null ||
		itemKind !== ALL;

	function clearFilters(): void {
		setQuery("");
		setStatus(ALL);
		setBrandId(ALL);
		setCategoryId(null);
		setPickedCategoryName(null);
		setItemKind(ALL);
		setPage(1);
	}

	const productsQuery = useQuery({
		queryKey: ["admin-catalog-products", scoped],
		queryFn: () => listAdminProducts(scoped),
		// Keep the previous page on screen while the next one loads, matching both
		// sibling dashboards. Without it the table and pager unmount on every page
		// change and the layout jumps to a skeleton -- worst precisely where an
		// operator pages fastest.
		placeholderData: (previous) => previous,
	});

	const brandsQuery = useQuery({
		queryKey: ["admin-catalog-brands"],
		queryFn: listAdminBrands,
		staleTime: 5 * 60 * 1000,
	});

	/** Any filter change returns to page 1; page 7 of a new filter is nothing. */
	const onFilterChange =
		(setter: (value: string) => void) => (value: string) => {
			setter(value);
			setPage(1);
		};

	/*
	 * The search box resets the page TWICE, and the second one is the real one.
	 *
	 * Its `onChange` resets immediately, which keeps the pager honest while
	 * typing. But the request is built from `debouncedQuery`, so an operator who
	 * types and then clicks Next inside the 250ms window sets page back to 2, and
	 * the debounced value lands underneath it -- a new search, on page 2, with no
	 * indication why its first page was skipped.
	 *
	 * DURING RENDER, not in an effect. An effect runs after the commit, so React
	 * Query would already have seen `{ q: "tile", page: 2 }`, fired that request,
	 * and only then been corrected -- the operator gets a flash of the wrong page
	 * and the server gets a query nobody wanted. Adjusting here means the render
	 * that first carries the new query also carries page 1, so the bad
	 * combination never exists. React re-runs the component immediately and bails
	 * out when the value is already 1, so the ordinary path costs nothing.
	 */
	const [lastDebouncedQuery, setLastDebouncedQuery] = useState(debouncedQuery);
	if (debouncedQuery !== lastDebouncedQuery) {
		setLastDebouncedQuery(debouncedQuery);
		setPage(1);
	}

	const result = productsQuery.data;
	const products = result?.items ?? [];

	/*
	 * The selection is dropped whenever the visible set changes.
	 *
	 * `scoped` is the resolved filter set the query actually ran with, so this
	 * keys on what is ON SCREEN rather than on what was requested -- a filter the
	 * scoping rejected must not leave a stale selection behind either. Ticking
	 * rows, filtering to something else and pressing Publish is otherwise an
	 * action over products the operator cannot see, and the audit would record
	 * the change without recording that nobody meant it.
	 */
	/*
	 * TWO KEYS, because the two selection modes are undone by different things.
	 *
	 * Ticked rows cannot outlive the rows themselves, so they clear on a filter
	 * change AND on a page change -- the ids are only meaningful against what was
	 * on screen when they were ticked.
	 *
	 * Matching mode is the FILTER, and paging does not change the filter. It used
	 * to be dropped by both, which made the mode unusable for what it is for:
	 * clicking Next to check what you had selected destroyed the selection, and
	 * excluding a product on page 2 of 342 was impossible. The plan makes exactly
	 * this argument about page SIZE -- "changes the window, not membership" -- and
	 * the page number is the same argument.
	 */
	const { page: _keyPage, perPage: _keyPerPage, ...scopedFilterOnly } = scoped;
	// `scoped` carries `page` and `perPage`, so keying on it whole made the
	// filter key change on every pager click -- which is the behaviour this split
	// exists to end. The window fields come out; what is left is the filter.
	const filterKey = JSON.stringify(scopedFilterOnly);
	const visibleKey = `${filterKey}:${page}`;
	const [lastFilterKey, setLastFilterKey] = useState(filterKey);
	const [lastVisibleKey, setLastVisibleKey] = useState(visibleKey);
	const filterChanged = lastFilterKey !== filterKey;
	const visibleChanged = lastVisibleKey !== visibleKey;
	if (visibleChanged) {
		setLastVisibleKey(visibleKey);
		if (bulkResult) setBulkResult(null);
	}
	if (filterChanged) {
		setLastFilterKey(filterKey);
		/*
		 * Matching mode goes with the filter. See the state declaration:
		 * re-scoping to a new filter is the one path by which a selection grows
		 * without the operator reading the new count.
		 */
		if (matchingAll) {
			setMatchingAll(false);
			setExcluded(new Set());
		}
		/*
		 * And so does an OPEN CONFIRMATION. Dropping the selection while leaving
		 * the dialog up moved the accident one layer along: its ids stayed frozen
		 * against the old filter and, once the filter summary was frozen too, its
		 * sentence described a scope the page was no longer showing. Confirming
		 * that is the thing this whole unit exists to prevent.
		 */
		if (pendingBulk) setPendingBulk(null);
	}

	/*
	 * ONE write to `selected` per render, from ONE derivation.
	 *
	 * There were two: the clear above, and a prune to the rendered ids below.
	 * Both were conditional `setSelected(value)` calls on the same hook in the
	 * same render pass, so the second silently replaced the first -- the prune
	 * recomputed from a `selected` that still held the pre-clear value and put a
	 * selection back that the filter change had just dropped. Two fixes, each
	 * correct alone, cancelling in the overlap nobody reviewed.
	 *
	 * Both rules survive, composed instead of racing:
	 *
	 * - A CHANGED VISIBLE KEY drops everything. Ticking rows, filtering to
	 *   something else and pressing Publish is otherwise an action over products
	 *   the operator cannot see, and the audit would record the change without
	 *   recording that nobody meant it.
	 * - AN UNCHANGED KEY still prunes to what is rendered, because the key is
	 *   built from the REQUEST and the same request returns different rows on
	 *   this screen: the list orders by `updatedAt`, a bulk edit bumps it, and
	 *   the invalidation this page performs after its own mutation reshuffles
	 *   the page under a selection that survived deliberately.
	 *
	 * The `.length !== .size` guard is what terminates it. Every branch reaches a
	 * render where the survivors are exactly the current selection, and no
	 * further write is issued.
	 */
	/*
	 * The filter in words, for the confirmation. "342 products" is a number; "342
	 * draft products in Tiles" is something an operator can check against what
	 * they meant, and is the sentence the audit was said to be missing.
	 */
	const brandLabel = (brandsQuery.data ?? []).find(
		(brand) => brand.id === brandId,
	)?.name;
	const filterSummary =
		[
			status !== ALL ? `status ${status}` : null,
			itemKind !== ALL ? `item kind ${itemKind}` : null,
			/*
			 * Keyed on the ID, not on the name. `categoryLabel` is null until the
			 * taxonomy lookup for a deep link settles, and stays null forever if it
			 * fails -- so keying on the name dropped the category clause entirely
			 * and the dialog told an operator arriving at `?categoryId=...` that
			 * they were about to publish "the whole catalogue". Understating the
			 * scope is the safe direction and the sentence was still false, which
			 * for the one control whose entire job is the sentence is the whole
			 * defect.
			 */
			categoryId ? `category ${categoryLabel ?? "(still loading)"}` : null,
			brandId !== ALL ? `brand ${brandLabel ?? "(still loading)"}` : null,
			debouncedQuery ? `a search for "${debouncedQuery}"` : null,
		]
			.filter(Boolean)
			.join(", ") || "no filter (the whole catalogue)";

	const visibleIds = new Set(products.map((product) => product.id));
	const survivors = visibleChanged
		? []
		: [...selected].filter((id) => visibleIds.has(id));
	/*
	 * The prune is for TICKED ROWS only. In matching mode the selection is the
	 * filter, not a list of ids, so pruning it to what is rendered would shrink
	 * "all 342 matching" to "the 25 on this page" on every refetch -- silently,
	 * and in the direction that looks like it worked.
	 */
	if (!matchingAll && survivors.length !== selected.size) {
		setSelected(new Set(survivors));
	}

	const toggleOne = (id: string) => {
		/*
		 * In matching mode the tick means the opposite: the selection is the
		 * filter, so unticking a row REMOVES it from that set rather than adding
		 * it to a list. "All 342 except these three" is the shape.
		 */
		if (matchingAll) {
			setExcluded((current) => {
				const next = new Set(current);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			return;
		}
		setSelected((current) => {
			const next = new Set(current);
			if (!next.delete(id)) next.add(id);
			return next;
		});
	};

	/*
	 * MEMBERSHIP, not size -- and the same test the header checkbox renders.
	 *
	 * `selected.size === products.length` agrees with the header only while every
	 * selected id is on the page, and it stops agreeing the moment one is not.
	 * Matching mode produces exactly that: the set the header reads there is
	 * "every visible row except the exclusions", which has nothing to do with the
	 * size of `selected`. The checkbox would show one state and clicking it would
	 * do the other.
	 */
	/** What the row checkbox shows. In matching mode: everything but exclusions. */
	const isRowSelected = (id: string) =>
		matchingAll ? !excluded.has(id) : selected.has(id);

	const allOnPageSelected =
		products.length > 0 &&
		products.every((product) => isRowSelected(product.id));

	const toggleAll = () => {
		/*
		 * Matching mode again inverts it, exactly as `toggleOne` does. This wrote
		 * only to `selected`, which matching mode ignores -- so the header
		 * checkbox was a control that visibly did nothing, while quietly filling a
		 * set the operator could not see. Pressing Clear afterwards then left
		 * twenty-five rows ticked, because Clear resets the mode and not that set.
		 */
		if (matchingAll) {
			setExcluded(
				allOnPageSelected
					? new Set(products.map((product) => product.id))
					: new Set(),
			);
			return;
		}
		setSelected(
			allOnPageSelected
				? new Set()
				: new Set(products.map((product) => product.id)),
		);
	};

	const bulk = useMutation({
		mutationFn: ({
			ids,
			patch,
			intent,
		}: {
			ids: string[];
			patch: BulkProductPatch;
			intent: string;
		}) => bulkUpdateProducts(ids, patch, intent),
		onSuccess: async (result) => {
			setBulkResult(result);
			/*
			 * THE SELECTION IS CLEARED, and the refusals stand on their own.
			 *
			 * This used to re-select the refused products, so the operator could
			 * fix and retry without finding them again. That worked while a bulk
			 * was one page: the very next render prunes the selection to the ids
			 * currently rendered, so at 500 refusals spread over twenty pages
			 * roughly 475 of them vanished without a trace. The retry it promised
			 * was only ever available for the handful that happened to be on
			 * screen.
			 *
			 * The refusal list below carries product NAMES now, which is what an
			 * operator needs to go and fix them -- and it does not quietly lose
			 * nineteen twentieths of itself.
			 */
			setSelected(new Set());
			setMatchingAll(false);
			setExcluded(new Set());
			await queryClient.invalidateQueries({
				queryKey: ["admin-catalog-products"],
			});
		},
		// Closed on settle rather than on click, so the dialog can show that it is
		// working and can hand focus back to a trigger that is enabled again. On
		// SETTLE and not on success: a failed bulk must not leave it open over a
		// toast the operator cannot reach.
		onSettled: () => setPendingBulk(null),
	});

	/** How many products the pending action would touch. */
	const selectionCount = matchingAll
		? Math.max((result?.total ?? 0) - excluded.size, 0)
		: selected.size;

	/**
	 * What the patch DOES, in words, for the confirmation.
	 *
	 * The bulk form was handing every one of its five fields to the dialog as
	 * "Apply change", so the widest path on the screen -- the only route to
	 * category, brand and verification -- rendered "Apply change for 500 products
	 * matching status draft" and a confirm button reading the same. Moving 500
	 * products onto a withdrawn brand takes them off sale, which is why the
	 * server prices `brandId` at publish; the sentence has to say which brand.
	 */
	function describePatch(patch: BulkProductPatch, valueLabel?: string): string {
		if (patch.status === "active") return "Publish";
		if (patch.status === "draft") return "Unpublish";
		if (patch.status) return `Set status to ${patch.status}`;
		if (patch.tier) return `Set tier to ${patch.tier}`;
		if (patch.verification)
			return `Set verification to ${patch.verification.replace(/_/g, " ")}`;
		/*
		 * `valueLabel` comes from the FORM, which owns the picker that chose it.
		 * Reading `pickedCategoryName` here named the category the list is
		 * FILTERED to -- a different thing entirely, and null in the ordinary
		 * case, so a re-categorisation announced itself as "Move to the chosen
		 * category" while the filter's own category sat in the same sentence.
		 */
		if (patch.categoryId)
			return `Move to ${valueLabel ?? "the chosen category"}`;
		if (patch.brandId)
			return `Set brand to ${valueLabel ?? "the chosen brand"}`;
		// Unreachable while `BulkProductPatch` has five optional fields and the
		// form refuses to apply with none chosen -- but a silent "" would be a
		// confirm button with no label, so it says something rather than nothing.
		return "Apply change";
	}

	/*
	 * Resolve, then ASK -- never resolve and act.
	 *
	 * The sentence is frozen whole when the dialog opens: count, filter wording
	 * and all. That is what the operator read before deciding, and nothing that
	 * happens afterwards -- a refetch, a filter change -- may edit any part of it
	 * underneath them.
	 */
	async function requestBulk(
		patch: BulkProductPatch,
		label: string,
	): Promise<void> {
		setResolveError(null);
		// Only a publish can be refused per product; see `mayRefuse`.
		const mayRefuse = patch.status === "active";
		if (!matchingAll) {
			setPendingBulk({
				patch,
				ids: [...selected],
				summary: {
					label,
					count: selected.size,
					total: selected.size,
					excludedCount: 0,
					filterSummary,
					mayRefuse,
					resolvedAt: new Date(),
				},
			});
			return;
		}

		/*
		 * REFUSE BEFORE RESOLVING. `result.total` already says how many match, so
		 * a filter twice the cap can be turned down without twenty round-trips
		 * whose only purpose is to arrive at a number the page was already
		 * holding. The dialog needs the count, not the ids, to say no.
		 *
		 * On `total` rather than on `total - excluded`, and the refusal says so:
		 * the cap is a limit on what can be ENUMERATED, and unticking rows does
		 * not shrink the number of pages that have to be read to find them.
		 */
		if ((result?.total ?? 0) > MAX_BULK_SELECTION) {
			setPendingBulk({
				patch,
				ids: [],
				summary: {
					label,
					count: 0,
					total: result?.total ?? 0,
					excludedCount: 0,
					filterSummary,
					mayRefuse,
					resolvedAt: new Date(),
				},
			});
			return;
		}

		/*
		 * ONE RESOLVE AT A TIME. Five sequential round-trips is long enough that
		 * an unguarded button reads as broken, so a second click used to start a
		 * second resolve -- and a click on a DIFFERENT verb rewrote the label and
		 * the patch under an already-open dialog, changing what an operator was
		 * midway through reading.
		 */
		if (resolving) return;
		/*
		 * A BACKSTOP, and a disclosed one: the guard the operator actually meets
		 * is `disabled={... || resolving}` on the triggers, and that is what the
		 * suite pins. React flushes the state between two dispatched clicks, so
		 * the second lands on a disabled button and this line is never the thing
		 * that stops it -- no test here can make it fail. It is kept for a caller
		 * that is not a button press, not claimed as covered.
		 */
		setResolving(true);
		try {
			const resolved = await resolveSelection(scoped);
			const ids = resolved.ids.filter((id) => !excluded.has(id));
			setPendingBulk({
				patch,
				ids,
				summary: {
					label,
					count: ids.length,
					total: resolved.total,
					excludedCount: resolved.ids.length - ids.length,
					filterSummary,
					mayRefuse,
					resolvedAt: new Date(),
				},
			});
		} catch (error) {
			setResolveError(
				apiErrorMessage(error, "Could not work out which products match."),
			);
		} finally {
			setResolving(false);
		}
	}
	const totalPages = Math.max(1, result?.totalPages ?? 1);
	const currentPage = Math.min(page, totalPages);
	/*
	 * True while a refetch is in flight with the previous page still on screen.
	 * `placeholderData` is what keeps the table from unmounting, and the cost of
	 * that is a table which can be showing the PREVIOUS query's rows with nothing
	 * saying so. This is what says so.
	 *
	 * `isRefreshing`, NOT `isStale`: this file also renders `isPriceStale`, which
	 * is a completely different fact -- a price past its category's refresh
	 * cadence, which is about the data rather than the request. Two things called
	 * stale, one row apart, is how the wrong one gets read at a glance.
	 */
	const isRefreshing = productsQuery.isFetching && !productsQuery.isPending;

	return (
		<div className="grid gap-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="grid gap-2">
					<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
						Catalogue
					</p>
					<h2 className="text-2xl font-semibold tracking-tight">Products</h2>
					<p className="text-sm text-muted-foreground">
						Every product, drafts included. Public browse only shows active
						products, so this is the only view of work in progress.
					</p>
				</div>
				{/*
				 * Hidden without `admin:catalog:write`, like the editor's publish
				 * button is hidden without `admin:catalog:publish`. A read-only
				 * operator offered a New product button gets a form they cannot
				 * submit, which is worse than no button at all.
				 */}
				{canWrite ? (
					<Button asChild>
						<Link to="/catalog/products/new">New product</Link>
					</Button>
				) : null}
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>All products</CardTitle>
					<CardDescription>
						{result
							? `${result.total.toLocaleString("en-IN")} product${result.total === 1 ? "" : "s"} matching`
							: "Filter by status or brand, or search by name and SKU."}
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-3 rounded-2xl border bg-slate-50/80 p-3 md:grid-cols-[minmax(0,1fr)_150px_160px_180px_minmax(0,1fr)]">
						<Input
							placeholder="Search by name or SKU"
							value={query}
							onChange={(event) => {
								setQuery(event.target.value);
								setPage(1);
							}}
						/>
						<Select
							value={itemKind}
							onValueChange={onFilterChange(setItemKind)}
						>
							<SelectTrigger aria-label="Item kind">
								<SelectValue placeholder="Item kind" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value={ALL}>All types</SelectItem>
									{ITEM_KINDS.map((kind) => (
										<SelectItem key={kind} value={kind}>
											{ITEM_KIND_LABELS[kind]}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
						<Select value={status} onValueChange={onFilterChange(setStatus)}>
							{/*
							 * `aria-label`, because a Radix trigger's accessible name comes
							 * from its VALUE, not from the placeholder -- so with "All
							 * statuses" selected the control announces the value and nothing
							 * about what it controls. Screen readers and tests hit the same
							 * wall for the same reason.
							 */}
							<SelectTrigger aria-label="Status">
								<SelectValue placeholder="Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value={ALL}>All statuses</SelectItem>
									<SelectItem value="draft">Draft</SelectItem>
									<SelectItem value="active">Active</SelectItem>
									<SelectItem value="inactive">Inactive</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
						<Select value={brandId} onValueChange={onFilterChange(setBrandId)}>
							<SelectTrigger aria-label="Brand">
								<SelectValue placeholder="Brand" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value={ALL}>All brands</SelectItem>
									{/*
									 * A failed brand fetch leaves this list empty, which reads as
									 * "there are no brands" rather than "we could not load them".
									 * The product table is unaffected, so this degrades to a
									 * labelled dead option rather than taking the screen down.
									 */}
									{brandsQuery.isError ? (
										<SelectItem value="__unavailable__" disabled>
											Brands unavailable
										</SelectItem>
									) : null}
									{(brandsQuery.data ?? []).map((brand) => (
										<SelectItem key={brand.id} value={brand.id}>
											{brand.name}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
						{/*
						 * `aria-label` on the input via `id` + a visually-hidden label
						 * would be the other option; `CategoryPicker` takes an `id` for
						 * exactly that, and the sibling selects solve the same problem
						 * with `aria-label` on the trigger. Kept consistent with them.
						 */}
						<CategoryPicker
							id="product-category-filter"
							label="Filter by category"
							value={categoryId}
							valueLabel={categoryLabel}
							onChange={(category) => {
								setCategoryId(category?.id ?? null);
								setPickedCategoryName(category?.name ?? null);
								setPage(1);
							}}
						/>
					</div>

					{productsQuery.isPending ? (
						<div className="grid gap-2">
							{["a", "b", "c", "d", "e"].map((key) => (
								<Skeleton key={key} className="h-12 w-full" />
							))}
						</div>
					) : productsQuery.isError ? (
						<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
							Could not load products.{" "}
							<button
								type="button"
								className="underline"
								onClick={() => productsQuery.refetch()}
							>
								Try again
							</button>
						</div>
					) : products.length === 0 ? (
						/*
						 * Two different nothings. "No products match these filters" over an
						 * empty catalogue sends an operator hunting for a filter they never
						 * set; "the catalogue is empty" over a narrowed search hides the
						 * filter that is doing it. The way out has to be offered too --
						 * without it the operator has to guess which control to undo.
						 */
						<div className="grid justify-items-center gap-2 rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
							{hasFilters ? (
								<>
									<p>No products match these filters.</p>
									<button
										type="button"
										className="underline"
										onClick={clearFilters}
									>
										Clear filters
									</button>
								</>
							) : (
								<p>
									No products in the catalogue yet. Imported products appear
									here, drafts included.
								</p>
							)}
						</div>
					) : (
						<>
							{/*
							 * `matchingAll` too, and not just a non-empty `selected`. Entering
							 * matching mode empties `selected` -- the selection is the filter
							 * there, not a list of ids -- so a bar gated on that set alone
							 * vanishes at the exact moment the operator selects the most.
							 */}
							{matchingAll || selected.size > 0 ? (
								<section
									className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border bg-slate-50 p-3 text-sm"
									aria-label="Bulk actions"
								>
									{/*
									 * ANNOUNCED, because the control that changes it is not the one
									 * that reports it. Unticking a row moves this number and nothing
									 * about the checkbox says so, so an operator working by screen
									 * reader narrows a 342-product selection with no confirmation
									 * that it narrowed.
									 *
									 * `polite`: it is a running total during a sequence of ticks, not
									 * an interruption. The confirmation dialog states the final number
									 * before anything is sent.
									 */}
									<span aria-live="polite" className="font-medium">
										{matchingAll
											? `All ${selectionCount.toLocaleString("en-IN")} matching selected`
											: `${selected.size} selected on this page`}
									</span>
									{/*
									 * THE OFFER, and only when it means something. With every
									 * match already on the page there is nothing to extend to,
									 * and an offer that changes nothing reads as a control that
									 * does not work.
									 */}
									{!matchingAll && (result?.total ?? 0) > products.length ? (
										<Button
											variant="link"
											size="sm"
											onClick={() => {
												setMatchingAll(true);
												setExcluded(new Set());
												setSelected(new Set());
											}}
										>
											Select all {(result?.total ?? 0).toLocaleString("en-IN")}{" "}
											matching
										</Button>
									) : null}
									{matchingAll ? (
										<Button
											variant="link"
											size="sm"
											// Named, because a bare "Clear" beside "All 40 matching
											// selected" does not say WHAT it clears -- and the row
											// checkbox next to it also announces itself as "Clear
											// selection" when every visible row is in.
											aria-label="Clear the whole selection"
											onClick={() => {
												setMatchingAll(false);
												setExcluded(new Set());
												/*
												 * `selected` is deliberately NOT reset here. It was,
												 * briefly: a reviewer found that Clear left rows
												 * ticked, because the header checkbox used to write
												 * to `selected` while matching mode read `excluded`.
												 * That was one defect with two faces, and fixing the
												 * checkbox closed both -- entering matching mode
												 * empties `selected`, and with `toggleOne` and
												 * `toggleAll` both diverting to `excluded` there is
												 * no writer left to refill it. A second reset here
												 * would be a line no change to this file could make
												 * fail.
												 */
											}}
										>
											Clear
										</Button>
									) : null}
									<div className="ml-auto flex flex-wrap items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={!canWrite || bulk.isPending || resolving}
											onClick={() =>
												requestBulk({ tier: "premium" }, "Set tier: premium")
											}
										>
											Set tier: premium
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={!canPublish || bulk.isPending || resolving}
											onClick={() =>
												requestBulk({ status: "active" }, "Publish")
											}
										>
											Publish
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={!canPublish || bulk.isPending || resolving}
											onClick={() =>
												requestBulk({ status: "draft" }, "Unpublish")
											}
										>
											Unpublish
										</Button>
										{!canPublish ? (
											<span className="text-xs text-muted-foreground">
												Publishing needs admin:catalog:publish.
											</span>
										) : null}
										<Button
											variant="ghost"
											size="sm"
											disabled={bulk.isPending}
											onClick={() => setSelected(new Set())}
										>
											Clear
										</Button>
									</div>
									{/*
									 * THE SHORTCUTS ABOVE ARE NOT THE LIMIT.
									 *
									 * Those three buttons cover two of the five fields the bulk
									 * endpoint accepts, and neither at full range. This reaches
									 * the rest -- including `categoryId`, the verb plan 008's V5
									 * names and the one the screen could not express at all.
									 */}
									<div className="w-full border-t pt-2">
										<BulkChangeForm
											brands={brandsQuery.data ?? []}
											permissions={operatorPermissions}
											disabled={bulk.isPending || resolving}
											onApply={(patch, valueLabel) =>
												requestBulk(patch, describePatch(patch, valueLabel))
											}
										/>
									</div>
								</section>
							) : null}

							{resolveError ? (
								<p
									role="alert"
									className="mb-3 rounded-2xl border border-red-300 bg-red-50 p-3 text-sm text-red-900"
								>
									{resolveError}
								</p>
							) : null}

							<BulkConfirmDialog
								open={pendingBulk !== null}
								summary={pendingBulk?.summary ?? null}
								cap={MAX_BULK_SELECTION}
								pending={bulk.isPending}
								onCancel={() => setPendingBulk(null)}
								/*
								 * IT STAYS OPEN while the mutation runs, and the mutation's
								 * own settle closes it. Closing here, synchronously, meant the
								 * dialog unmounted before `isPending` was ever true: the
								 * "Applying..." label and the disabled Cancel were unreachable
								 * branches, and the focus restore fired at the trigger in the
								 * same tick that disabled it -- so confirming, unlike
								 * cancelling, dropped focus onto <body>. The docblock claiming
								 * focus goes back was true for one of the two exits.
								 */
								onConfirm={() => {
									if (!pendingBulk || bulk.isPending) return;
									bulk.mutate({
										ids: pendingBulk.ids,
										patch: pendingBulk.patch,
										// The sentence they just read, sent as it was read.
										intent: bulkIntentSentence(pendingBulk.summary),
									});
								}}
							/>

							{bulkResult ? (
								/*
								 * The refusals are NAMED, not just counted.
								 *
								 * A bulk publish over a real range is expected to skip some, and
								 * "3 skipped" sends the operator back to find which three. Every
								 * refused product is named, and the skipped ones stay selected, so
								 * fixing and retrying does not mean hunting for them again.
								 *
								 * `BulkRefusalList` owns the shape: the gate's own sentence heads a
								 * group and the names sit under it, rather than each row repeating
								 * the reason. The count in the line above stays the TOTAL, since a
								 * sum split across groups is not one anybody can add up.
								 */
								<div
									role="status"
									className="mb-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
								>
									<p>
										{bulkResult.updated} product
										{bulkResult.updated === 1 ? "" : "s"} updated
										{bulkResult.skipped.length > 0
											? `, ${bulkResult.skipped.length} left unchanged:`
											: "."}
									</p>
									{bulkResult.skipped.length > 0 ? (
										<BulkRefusalList skips={bulkResult.skipped} />
									) : null}
								</div>
							) : null}

							<DataTable
								rows={products}
								rowKey={(product) => product.id}
								isRefreshing={isRefreshing}
								selection={{
									/*
									 * DERIVED in matching mode: the checkbox state is "every
									 * visible row except the ones unticked", because the
									 * selection there is the filter rather than a list of ids.
									 * Passing `selected` would render every box empty while the
									 * bar says all 342 are selected.
									 */
									selected: matchingAll
										? new Set(
												products
													.filter((product) => isRowSelected(product.id))
													.map((product) => product.id),
											)
										: selected,
									onToggle: toggleOne,
									onToggleAll: toggleAll,
									label: (product) => `Select ${product.name}`,
									/*
									 * The default wording is false in both states once the
									 * selection is a filter: "Clear selection" would clear this
									 * page and leave 340 products selected, and "Select every
									 * product on this page" describes lifting an exclusion.
									 */
									allLabel: (allSelected) =>
										matchingAll
											? allSelected
												? "Exclude every product on this page"
												: "Stop excluding the products on this page"
											: allSelected
												? "Clear selection"
												: "Select every product on this page",
									// Some of the wider selection is on this page and some of it
									// is excluded -- the third state a boolean cannot express.
									partial:
										matchingAll &&
										excluded.size > 0 &&
										products.some((product) => !excluded.has(product.id)),
								}}
								onRowActivate={(product) =>
									navigate(`/catalog/products/${product.id}`)
								}
								rowLabel={(product) => `Edit ${product.name}`}
								columns={[
									{
										header: "Product",
										cell: (product) => (
											<>
												<div className="font-medium">{product.name}</div>
												<div className="text-xs text-muted-foreground">
													{product.uom}
												</div>
											</>
										),
									},
									{
										header: "Brand",
										cell: (product) => product.brandName ?? "—",
									},
									{
										header: "SKU",
										cell: (product) => product.sku ?? "—",
										cellClassName: "font-mono text-xs",
									},
									{
										header: "Status",
										cell: (product) => <StatusBadge status={product.status} />,
									},
									{
										header: "Tier",
										cell: (product) => product.tier,
										cellClassName: "capitalize",
									},
									{
										header: "Price",
										align: "right",
										cellClassName: "tabular-nums",
										cell: (product) => (
											<>
												{priceLabel(product)}
												{isPriceStale(product) ? (
													<span
														className="ml-1 text-xs text-amber-700"
														title="Past this category's refresh cadence"
													>
														stale
													</span>
												) : null}
											</>
										),
									},
								]}
							/>
						</>
					)}

					{result && totalPages > 1 ? (
						/*
						 * The pager reads `page` -- ITS OWN STATE -- not `result.page`.
						 *
						 * `result` is the previous page while the next one loads, which is
						 * the whole point of `placeholderData`. The guards used to read that
						 * stale echo, so on page 7 of 8 a second Next click still found the
						 * button enabled at `result.page === 7` and stepped local state to 9.
						 * The screen then said "Page 9 of 8" above "No products match these
						 * filters", under a header still reading "200 products matching".
						 *
						 * Reading local state makes the guard current, and clamping the step
						 * makes it safe even if a click slips through.
						 *
						 * The clamp is DELIBERATE REDUNDANCY and cannot be shown to work on
						 * its own: with the guard in place no click reaches it, so mutating
						 * `Math.min` away leaves every test green. Mutating both together
						 * does go red, which is the honest statement of what is covered --
						 * the pair prevents the overshoot, and the clamp is the half that
						 * survives someone later loosening the guard.
						 */
						<div className="flex flex-col gap-3 border-t pt-3 text-sm sm:flex-row sm:items-center sm:justify-between">
							<span className="flex flex-wrap items-center gap-3 text-muted-foreground">
								<span>
									Showing{" "}
									{result.total === 0
										? 0
										: (result.page - 1) * result.perPage + 1}{" "}
									to {Math.min(result.page * result.perPage, result.total)} of{" "}
									{result.total.toLocaleString("en-IN")} products
								</span>
								<span>
									Page {result.page} of {totalPages}
								</span>
								{/*
								 * BACK TO PAGE 1 on every change, which is the part that is
								 * not obvious. Page 3 of 25-row pages is rows 51-75; page 3
								 * of 100-row pages is rows 201-300. Keeping the number would
								 * move an operator somewhere they did not ask to go and take
								 * their ticked rows off screen on the way.
								 *
								 * Matching mode survives it, because the filter has not
								 * changed -- `perPage` and `page` are both outside the key
								 * that drops it. That is the whole distinction: page size
								 * changes the window, not the membership.
								 */}
								<label className="flex items-center gap-2">
									Rows per page
									<select
										aria-label="Rows per page"
										className="rounded-md border bg-transparent px-2 py-1"
										value={perPage}
										onChange={(event) => {
											setPerPage(Number(event.target.value) as PageSize);
											setPage(1);
										}}
									>
										{PAGE_SIZES.map((size) => (
											<option key={size} value={size}>
												{size}
											</option>
										))}
									</select>
								</label>
							</span>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage <= 1 || isRefreshing}
									onClick={() =>
										setPage((value) =>
											Math.max(1, Math.min(totalPages, value) - 1),
										)
									}
								>
									Previous
								</Button>
								<div className="flex items-center gap-1">
									{getPageNumbers(currentPage, totalPages).map((pageNum) => (
										<Button
											key={pageNum}
											variant={pageNum === currentPage ? "default" : "outline"}
											size="sm"
											disabled={isRefreshing}
											className="h-8 min-w-8 px-2"
											aria-label={`Page ${pageNum}`}
											aria-current={
												pageNum === currentPage ? "page" : undefined
											}
											onClick={() => setPage(pageNum)}
										>
											{pageNum}
										</Button>
									))}
								</div>
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage >= totalPages || isRefreshing}
									onClick={() =>
										setPage((value) =>
											Math.min(totalPages, Math.max(1, value) + 1),
										)
									}
								>
									Next
								</Button>
							</div>
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
